/*
  Ever Leaf analytics adapter — provider-neutral.

  Listens for Ever Leaf domain signals on `document` and republishes a small
  allowlisted payload as Shopify custom events (Shopify.analytics.publish).
  It knows nothing about Meta or any other provider: delivery happens in the
  Shopify Customer Events custom pixel (docs/analytics/).

  Signals consumed:
    ever-leaf:local-photo-ready          (assets/ever-leaf-personalizer.js,
                                          existing, after a successful decode)
    ever-leaf:personalization-committed  (assets/ever-leaf-personalizer.js,
                                          runCommit's successful commit point,
                                          detail: { source, operationId,
                                          hasDedication, dedicationLayout })

  Shopify custom events published:
    ever_leaf_personalizer_started       once per page load
    ever_leaf_personalization_committed  once per commit operation

  Standard commerce events (page_viewed, product_viewed,
  product_added_to_cart, checkout_started, checkout_completed) are owned by
  Shopify and are never emitted here.

  Privacy: payloads are built field by field from the allowlist below. No
  photo, file, upload URL, dedication text, line-item property, form data or
  raw Personalizer state is ever read or sent, and nothing is persisted.

  Fail-open: every step is wrapped; if Shopify.analytics is unavailable the
  adapter does nothing. It never touches the commerce flow.
*/
(function () {
  'use strict';

  if (window.EverLeafAnalytics) return;

  var SCHEMA_VERSION = 1;
  var EVENT_NAMES = {
    started: 'ever_leaf_personalizer_started',
    committed: 'ever_leaf_personalization_committed'
  };
  var SOURCES = { 'add-to-cart': 'add_to_cart', 'buy-now': 'buy_now' };
  var LAYOUTS = { together: 'together', split: 'split' };

  var startedPublished = false;
  var committedOperations = Object.create(null);

  function isDesignMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function randomEventId() {
    try {
      var bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      var hex = '';
      for (var i = 0; i < bytes.length; i++) hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
      return hex;
    } catch (e) {
      return String(Date.now()) + String(Math.random()).slice(2, 10);
    }
  }

  function numericId(value) {
    var text = value == null ? '' : String(value).trim();
    return /^[0-9]{1,20}$/.test(text) ? text : null;
  }

  function productRoot() {
    return document.querySelector('.ever-leaf-product[data-product-id]');
  }

  function productId(root) {
    return root ? numericId(root.getAttribute('data-product-id')) : null;
  }

  function variantId(root) {
    var sectionId = root && root.getAttribute('data-section-id');
    var form = sectionId ? document.getElementById('product-form-' + sectionId) : null;
    var input = form ? form.querySelector('input[name="id"]') : null;
    return input ? numericId(input.value) : null;
  }

  function basePayload() {
    var root = productRoot();
    return {
      schema_version: SCHEMA_VERSION,
      event_id: randomEventId(),
      product_id: productId(root),
      variant_id: variantId(root)
    };
  }

  function publish(name, payload) {
    try {
      var analytics = window.Shopify && window.Shopify.analytics;
      if (!analytics || typeof analytics.publish !== 'function') return false;
      var result = analytics.publish(name, payload);
      // Online store (observed in Shopify's web-pixels-manager): publish() is synchronous and returns a
      // boolean — `false` means Shopify REJECTED the event (invalid name or customData), so it must not be
      // recorded as delivered. Before the manager loads, Shopify's stub queues the event and returns true.
      // If a surface returns a Promise instead, it is never awaited (no blocking, no retry) — a rejection is
      // only observed so it cannot surface as an unhandled rejection.
      if (result && typeof result.then === 'function') {
        result.then(null, function () {});
        return true;
      }
      return result !== false;
    } catch (e) {
      return false;
    }
  }

  function onLocalPhotoReady() {
    try {
      if (startedPublished || isDesignMode()) return;
      // Mark as published only if publish() actually reached Shopify.analytics —
      // a signal that arrives while it is unavailable must not consume the
      // once-per-page slot (no retry, timer or wait: the next signal simply tries).
      if (publish(EVENT_NAMES.started, basePayload())) startedPublished = true;
    } catch (e) {
      /* analytics must never affect the Personalizer */
    }
  }

  function onPersonalizationCommitted(event) {
    try {
      if (isDesignMode()) return;
      var detail = (event && event.detail) || {};
      var source = SOURCES[detail.source];
      var operationId = detail.operationId;
      if (!source || typeof operationId !== 'number' || !(operationId > 0)) return;
      if (committedOperations[operationId]) return;

      var payload = basePayload();
      payload.source = source;
      payload.has_dedication = detail.hasDedication === true;
      if (payload.has_dedication && LAYOUTS[detail.dedicationLayout]) {
        payload.dedication_layout = LAYOUTS[detail.dedicationLayout];
      }
      // Same rule as Started: the operation is recorded only after a real publish.
      if (publish(EVENT_NAMES.committed, payload)) committedOperations[operationId] = true;
    } catch (e) {
      /* analytics must never affect the commerce flow */
    }
  }

  document.addEventListener('ever-leaf:local-photo-ready', onLocalPhotoReady);
  document.addEventListener('ever-leaf:personalization-committed', onPersonalizationCommitted);

  window.EverLeafAnalytics = { schemaVersion: SCHEMA_VERSION, eventNames: EVENT_NAMES };
})();
